// GST invoices
import apiClient from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { getErrorMessage } from '../utils/apiError';

const invoiceService = {
  // Invoice data for an order (null if not issued yet)
  getByOrder: async orderId => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.INVOICES.BY_ORDER(orderId));
      return { success: true, data: response.data?.data };
    } catch (error) {
      return {
        success: false,
        notFound: error.response?.status === 404,
        error: error.response?.data?.message || 'Invoice not available yet',
      };
    }
  },

  // Opens the printable invoice in a new tab (the request needs the auth header, so it's fetched first).
  // Call from a click handler; the tab is opened synchronously so pop-up blockers allow it.
  openInvoice: async orderId => {
    const tab = window.open('', '_blank');
    try {
      const response = await apiClient.get(API_ENDPOINTS.INVOICES.HTML(orderId), { responseType: 'text' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/html' }));
      if (tab) tab.location.href = url;
      else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return { success: true };
    } catch (error) {
      if (tab) tab.close();
      return { success: false, error: error.response?.status === 404 ? 'Invoice not available yet' : 'Could not open the invoice' };
    }
  },

  // Admin: issue the invoice for a paid order now
  issue: async orderId => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.INVOICES.ISSUE(orderId));
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Could not issue the invoice' };
    }
  },

  /**
   * Admin: download the GST invoice lines as a CSV file (GET /api/invoices/export?from&to, text/csv).
   * from/to are Date objects; `to` is exclusive. Leave both out to export every invoice.
   */
  exportCsv: async (from, to) => {
    try {
      const params = {};
      if (from) params.from = from.toISOString();
      if (to) params.to = to.toISOString();
      const response = await apiClient.get(API_ENDPOINTS.INVOICES.EXPORT, { params, responseType: 'blob' });
      const stamp = date => date.toISOString().slice(0, 10);
      const fileName = from || to
        ? `invoices-${from ? stamp(from) : 'start'}-to-${to ? stamp(new Date(to.getTime() - 1)) : 'today'}.csv`
        : 'invoices-all.csv';
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoking straight away can cancel the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      return { success: true, fileName };
    } catch (error) {
      // With responseType 'blob' the error body is a Blob too.
      let message = 'Could not export invoices';
      const body = error.response?.data;
      if (body instanceof Blob) {
        try {
          const parsed = JSON.parse(await body.text());
          if (parsed?.message) message = parsed.message;
        } catch {
          // not JSON; keep the generic message
        }
      } else if (error.response) {
        message = getErrorMessage(error, message);
      }
      return { success: false, error: message };
    }
  },
};

export default invoiceService;
